import type {
  SupplierInstallationDto,
  SupplierInstallationUpdatePayload,
} from '@/features/suppliers/lib/supplier-installation-types'
import type { InstallationGoodsFamily } from '@/features/installations/installation-create.schema'

export type SupplierInstallationFormState = {
  name: string
  localName: string
  goodsFamily: InstallationGoodsFamily | ''
  street: string
  street2: string
  streetNumber: string
  country: string
  postCode: string
  poBox: string
  city: string
  unlocode: string
  longitude: string
  latitude: string
  localStreet: string
  localStreet2: string
  localStreetNumber: string
  localPostCode: string
  localPoBox: string
  localCity: string
}

export const SUPPLIER_INSTALLATION_LOCAL_INPUT_TO_STATE: Record<
  string,
  keyof Pick<
    SupplierInstallationFormState,
    | 'localStreet'
    | 'localStreet2'
    | 'localStreetNumber'
    | 'localPostCode'
    | 'localPoBox'
    | 'localCity'
  >
> = {
  street: 'localStreet',
  streetNumber: 'localStreetNumber',
  street2: 'localStreet2',
  postCode: 'localPostCode',
  poBox: 'localPoBox',
  city: 'localCity',
}

export function buildSupplierInstallationFormState(
  installation: SupplierInstallationDto
): SupplierInstallationFormState {
  return {
    name: installation.name ?? '',
    localName: installation.localName ?? '',
    goodsFamily: (installation.goodsFamily as InstallationGoodsFamily | null) ?? '',
    street: installation.street ?? '',
    street2: installation.street2 ?? '',
    streetNumber: installation.streetNumber ?? '',
    country: installation.country ?? '',
    postCode: installation.postCode ?? '',
    poBox: installation.poBox ?? '',
    city: installation.city ?? '',
    unlocode: installation.unlocode ?? '',
    longitude:
      installation.longitude === null || installation.longitude === undefined
        ? ''
        : String(installation.longitude),
    latitude:
      installation.latitude === null || installation.latitude === undefined
        ? ''
        : String(installation.latitude),
    localStreet: installation.localStreet ?? '',
    localStreet2: installation.localStreet2 ?? '',
    localStreetNumber: installation.localStreetNumber ?? '',
    localPostCode: installation.localPostCode ?? '',
    localPoBox: installation.localPoBox ?? '',
    localCity: installation.localCity ?? '',
  }
}

function emptyToNull(value: string): string | null {
  return value.trim() === '' ? null : value
}

function optionalNumber(value: string): number | null {
  const trimmed = value.trim()
  if (trimmed === '') {
    return null
  }
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : null
}

export function buildSupplierInstallationUpdatePayload(
  state: SupplierInstallationFormState
): SupplierInstallationUpdatePayload {
  return {
    name: state.name.trim() || undefined,
    localName: emptyToNull(state.localName),
    goodsFamily: state.goodsFamily === '' ? null : state.goodsFamily,
    street: emptyToNull(state.street),
    street2: emptyToNull(state.street2),
    streetNumber: emptyToNull(state.streetNumber),
    country: emptyToNull(state.country),
    postCode: emptyToNull(state.postCode),
    poBox: emptyToNull(state.poBox),
    city: emptyToNull(state.city),
    unlocode: emptyToNull(state.unlocode),
    longitude: optionalNumber(state.longitude),
    latitude: optionalNumber(state.latitude),
    localStreet: emptyToNull(state.localStreet),
    localStreet2: emptyToNull(state.localStreet2),
    localStreetNumber: emptyToNull(state.localStreetNumber),
    localPostCode: emptyToNull(state.localPostCode),
    localPoBox: emptyToNull(state.localPoBox),
    localCity: emptyToNull(state.localCity),
  }
}
